/**
 * Canonical template identity, dimensions, and channel membership.
 *
 * This module must stay free of React, JSX, page components, and CSS so both
 * the browser and server can import the same data.
 *
 * A membership is a slot: one place on a page. It has one or more designs
 * (`members`), oldest first, and a page draws one of them. Identity lookups
 * know every member, so a saved scene keeps working whichever member is on
 * air (data/templateSlots.ts).
 */

import {DOT_NET_RELEASE_PRODUCTS} from "./dot-net-products";

export interface TemplateSize {
  width: number;
  height: number;
}

export interface TemplateDefinition extends TemplateSize {
  key: string;
  id: string;
  label: string;
  filename?: string;
  tooltip?: string;
  zoom: number;
  suffix?: string;
  displayDimensions?: string;
  nominalDimensions?: string;
}

export interface TemplateMembership {
  /** The member that code puts on air. */
  key: string;
  /**
   * Every design of this place, oldest first; it contains `key`. A new design
   * is a new member with its own key and id, never a change of an old one in
   * place: crops, cuts and texts are saved by id. Without `members` the place
   * has one design.
   */
  members?: readonly string[];
  /**
   * The design a saved scene draws when it has no member for this slot.
   * Defaults to the first member. Set this when restoring a design older
   * than the one already used by saved scenes.
   */
  sceneFallbackKey?: string;
  /** Releases: the set whose pages draw the banner. */
  set?: string;
  /**
   * Releases: a banner of no set. No page draws it until an admin adds it to
   * a page (stage, product, set, layout) in the Defaults panel, so a new
   * banner can go to one stage only. Give it no `set`.
   */
  optIn?: boolean;
  contentTypes?: string[];
  layouts?: number[];
  /**
   * Releases: the banner also draws on the upload layouts 5 and 6. Those
   * layouts draw only such banners, whatever their `layouts`.
   */
  uploadLayouts?: boolean;
  /**
   * Releases: the products whose pages draw the banner, when not every
   * product's page does (the .NET splashes). Unlike `products`, it changes
   * only what a page draws: every product still knows the id, so the eyes,
   * the crops and the lists of the channel keep it.
   */
  drawnFor?: readonly string[];
  /** Releases: the products whose pages do not draw the banner. */
  notDrawnFor?: readonly string[];
  /**
   * The products that show the template, when not every product does. A
   * channel can list two templates under one id only when no product shows
   * both: then a lookup by id needs the product.
   */
  products?: readonly string[];
  /** The products that do not show the template. */
  exceptProducts?: readonly string[];
}

export interface TemplateMetadata extends TemplateDefinition, TemplateMembership {
  dimensions: string;
  /** The keys of the slot's designs, oldest first. One key for a slot of one design. */
  members: readonly string[];
  /** The ids of the slot's designs, oldest first: an id of a saved list names the slot. */
  memberIds: readonly string[];
}

/** One place on a page and its designs. */
export interface TemplateSlot {
  channel: string;
  /** The position of the slot in the channel list. */
  index: number;
  /** The designs, oldest first. */
  members: readonly TemplateMetadata[];
  /** The design that code puts on air. */
  defaultMember: TemplateMetadata;
  /** The design saved scenes used before they recorded a member for this slot. */
  sceneFallbackMember: TemplateMetadata;
}

export const TEMPLATE_DEFINITIONS = [
  {
    key: "twitter",
    id: "Twitter",
    label: "SoMe Horizontal",
    width: 1200,
    height: 675,
    filename: "Twitter_Facebook_LinkedIn",
    tooltip: "BlueSky, Facebook, LinkedIn, X",
    zoom: 2.2,
  },
  {
    key: "you-tube-community",
    id: "YouTubeCommunity",
    label: "SoMe Square",
    width: 1080,
    height: 1080,
    filename: "YouTubeCommunity-Twitter-LinkedIn-Facebook",
    tooltip: "YouTube Community, BlueSky, Facebook, LinkedIn, X",
    zoom: 3,
  },
  {
    key: "so-me-vertical",
    id: "SoMeVertical",
    label: "SoMe Vertical",
    width: 1080,
    height: 1350,
    filename: "Twitter_Facebook_LinkedIn",
    tooltip: "BlueSky, Facebook, LinkedIn, X",
    zoom: 3,
  },
  {
    key: "instagram-stories",
    id: "InstagramStories",
    label: "Instagram Stories",
    width: 1080,
    height: 1920,
    zoom: 3,
  },
  {
    key: "instagram-feed",
    id: "InstagramFeed",
    label: "Instagram Feed",
    width: 1080,
    height: 1440,
    filename: "Instagram_Feed",
    tooltip: "Instagram Feed",
    zoom: 3,
  },
  {
    key: "blog-featured",
    id: "BlogFeatured",
    label: "Blog Featured",
    width: 1280,
    height: 720,
    zoom: 2,
  },
  {
    key: "blog-social-share",
    id: "BlogSocialShare",
    label: "Blog Social Share",
    width: 1280,
    height: 720,
    zoom: 2,
  },
  {
    key: "webpage-preview",
    id: "WebpagePreview",
    label: "Webpage Preview",
    width: 1280,
    height: 720,
    zoom: 2,
  },
  {
    key: "splash",
    id: "Splash",
    label: "Splash",
    width: 640,
    height: 400,
    filename: "Splash",
    zoom: 1,
  },
  {
    key: "splash-2-x",
    id: "Splash2x",
    label: "splash@2x",
    width: 1280,
    height: 800,
    filename: "Splash@2x",
    zoom: 2,
    suffix: "@2x",
  },
  // The 2026 splash design (2026.3 and later, pages/Releases/Splash2026): the
  // next design of the two splash places above, with its own keys and ids. A
  // new design never changes an old id, because crops, cuts and texts are
  // saved by id. The splash memberships list it in `members`.
  {
    key: "splash-2026",
    id: "Splash2026",
    label: "Splash",
    width: 640,
    height: 400,
    filename: "Splash",
    zoom: 1,
  },
  {
    key: "splash-2-x-2026",
    id: "Splash2x2026",
    label: "splash@2x",
    width: 1280,
    height: 800,
    filename: "Splash@2x",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "releases-email-01",
    id: "Email01",
    label: "Email01",
    width: 1300,
    height: 1300,
    filename: "Release_Email01",
    zoom: 2,
  },
  {
    key: "releases-email-02",
    id: "Email02",
    label: "Email02",
    width: 1300,
    height: 400,
    nominalDimensions: "650x200",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "releases-email-03",
    id: "Email03",
    label: "Email03",
    width: 1300,
    height: 650,
    nominalDimensions: "650x325",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "releases-email-04",
    id: "Email04",
    label: "Email04",
    width: 1300,
    height: 650,
    filename: "Release_Email04",
    zoom: 2,
  },
  {
    key: "horizontal-features",
    id: "HorizontalFeatures",
    label: "Horizontal Features",
    width: 1200,
    height: 675,
    filename: "Release_HorizontalFeatures",
    zoom: 2,
  },
  {
    key: "whats-new-preview",
    id: "WhatsNewPreview",
    label: "What's New Preview",
    width: 1280,
    height: 720,
    filename: "Release_WhatsNewPreview",
    zoom: 2,
  },
  {
    key: "wallpaper-1",
    id: "Wallpaper1",
    label: "Wallpaper",
    width: 1920,
    height: 1080,
    filename: "Release_Wallpaper1",
    zoom: 3,
  },
  {
    key: "wallpaper-2",
    id: "Wallpaper2",
    label: "Wallpaper 2",
    width: 1440,
    height: 900,
    filename: "Release_Wallpaper2",
    zoom: 2,
  },
  {
    key: "wallpaper-3",
    id: "Wallpaper3",
    label: "Wallpaper 3",
    width: 1366,
    height: 768,
    filename: "Release_Wallpaper3",
    zoom: 2,
  },
  {
    key: "wallpaper-preview",
    id: "WallpaperPreview",
    label: "Wallpaper Preview",
    width: 888,
    height: 475,
    filename: "Release_WallpaperPreview",
    zoom: 2,
  },
  {
    key: "dot-net-splash",
    id: "DotNetSplash",
    label: ".NET Splash",
    width: 512,
    height: 197,
    filename: "Release_DotNetSplash",
    zoom: 1,
  },
  {
    key: "dot-net-splash-2-x",
    id: "DotNetSplash2x",
    label: ".NET Splash @2x",
    width: 1024,
    height: 394,
    filename: "Release_DotNetSplash2x",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "dot-net-splash-legacy",
    id: "DotNetSplashLegacy",
    label: ".NET Splash (previous)",
    width: 512,
    height: 197,
    filename: "Release_DotNetSplash",
    zoom: 1,
  },
  {
    key: "dot-net-splash-2-x-legacy",
    id: "DotNetSplash2xLegacy",
    label: ".NET Splash @2x (previous)",
    width: 1024,
    height: 394,
    filename: "Release_DotNetSplash2x",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "screenshot",
    id: "Screenshot",
    label: "Screenshot",
    // 1280x720, like every other 16:9 template on the channel. Everything the
    // banner draws was scaled by the same 2/3 (pages/Releases/Screenshot and
    // layout/DraggableCursor, which only this template uses). The preview is
    // unchanged at 640px: 1920/3 and 1280/2 are the same number.
    width: 1280,
    height: 720,
    filename: "Release_Screenshot",
    zoom: 2,
  },
  // Release demo videos (pages/Releases/DemoVideo): the 1920x1080 frames of
  // the Figma handoff "CoverBaker / HANDOFF / IntelliJ IDEA" (file "YouTube",
  // node 12710:1400).
  {
    key: "demo-video-intro",
    id: "DemoVideoIntro",
    label: "Intro",
    width: 1920,
    height: 1080,
    filename: "Release_DemoVideo_Intro",
    zoom: 3,
  },
  {
    key: "demo-video-intro-demo",
    id: "DemoVideoIntroDemo",
    label: "Intro + Demo",
    width: 1920,
    height: 1080,
    filename: "Release_DemoVideo_IntroDemo",
    zoom: 3,
  },
  {
    key: "demo-video-demo",
    id: "DemoVideoDemo",
    label: "Demo",
    width: 1920,
    height: 1080,
    filename: "Release_DemoVideo_Demo",
    zoom: 3,
  },
  {
    key: "demo-video-code",
    id: "DemoVideoCode",
    label: "Code Demo",
    width: 1920,
    height: 1080,
    filename: "Release_DemoVideo_Code",
    zoom: 3,
  },
  {
    key: "livestream-horizontal",
    id: "LivestreamHorizontal",
    label: "YouTube, LinkedIn, X",
    width: 1280,
    height: 720,
    filename: "Livestream_Horizontal",
    zoom: 2.2,
  },
  {
    key: "livestream-square",
    id: "LivestreamSquare",
    label: "YouTube Community, Square",
    width: 1080,
    height: 1080,
    filename: "Livestream_Square",
    zoom: 3,
  },
  {
    key: "livestream-blog",
    id: "LivestreamBlog",
    label: "Featured Blog",
    width: 1280,
    height: 720,
    filename: "Livestream_Blog",
    zoom: 2.2,
  },
  {
    key: "livestream-wide",
    id: "LivestreamWide",
    label: "YouTube First Slide",
    width: 1920,
    height: 1080,
    filename: "Livestream_Wide",
    zoom: 2,
  },
  {
    key: "livestream-stories-intro",
    id: "LivestreamStoriesIntro",
    label: "Instagram Stories",
    width: 1080,
    height: 1920,
    filename: "Livestream_Stories_Intro",
    zoom: 3,
  },
  {
    key: "livestream-stories",
    id: "LivestreamStories",
    label: "Instagram Stories",
    width: 1080,
    height: 1920,
    filename: "Livestream_Stories",
    zoom: 3,
  },
  {
    key: "livestream-email",
    id: "LivestreamEmail",
    label: "Email Banner",
    width: 1300,
    height: 650,
    filename: "Livestream_Email_Banner",
    zoom: 2.2,
    suffix: "@2x",
  },
  {
    key: "blog-social-share-internal",
    id: "BlogSocialShareInternal",
    label: "Blog Social Share",
    width: 2560,
    height: 1440,
    nominalDimensions: "1280x720",
    filename: "Blog_Social_Share_Livestream",
    zoom: 3,
    suffix: "@2x",
  },
  {
    key: "email-01",
    id: "Email01",
    label: "Email",
    width: 1300,
    height: 192,
    nominalDimensions: "650x96",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "email-02",
    id: "Email02",
    label: "Email",
    width: 1300,
    height: 400,
    nominalDimensions: "650x200",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "email-03",
    id: "Email03",
    label: "Email",
    width: 1300,
    height: 650,
    nominalDimensions: "650x325",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "email-04",
    id: "Email04",
    label: "Email",
    width: 1300,
    height: 650,
    nominalDimensions: "650x325",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "voucher",
    id: "Voucher",
    label: "Voucher",
    width: 850,
    height: 550,
    zoom: 1.5,
  },
  {
    key: "landing-page",
    id: "LandingPage",
    label: "Landing Page Preview",
    width: 2560,
    height: 1440,
    nominalDimensions: "1280x720",
    zoom: 3,
    suffix: "@2x",
  },
  {
    key: "twitter-partners",
    id: "TwitterPartners",
    label: "SoMe Horizontal",
    width: 1200,
    height: 675,
    filename: "Twitter_Facebook_LinkedIn",
    tooltip: "BlueSky, Facebook, LinkedIn, X",
    zoom: 2,
  },
  {
    key: "instagram-stories-partners",
    id: "InstagramStoriesPartners",
    label: "Instagram Stories",
    width: 1080,
    height: 1920,
    zoom: 2,
  },
  {
    key: "instagram-post-partners",
    id: "InstagramPostPartners",
    label: "SoMe Square",
    width: 1080,
    height: 1080,
    filename: "Twitter_Facebook_LinkedIn",
    tooltip: "BlueSky, Facebook, LinkedIn, X, YouTube Community",
    zoom: 2,
  },
  {
    key: "instagram-feed-partners",
    id: "InstagramFeedPartners",
    label: "Instagram Feed",
    width: 1080,
    height: 1440,
    filename: "Instagram_Feed",
    tooltip: "Instagram Feed",
    zoom: 2,
  },
  {
    key: "so-me-vertical-partners",
    id: "SoMeVerticalPartners",
    label: "SoMe Vertical",
    width: 1080,
    height: 1350,
    filename: "Twitter_Facebook_LinkedIn",
    tooltip: "BlueSky, Facebook, Instagram Feed, LinkedIn, X",
    zoom: 2,
  },
  {
    key: "you-tube-thumbnail",
    id: "YouTubeThumbnail",
    label: "YouTube Thumbnail",
    width: 1280,
    height: 720,
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "you-tube-first-slide-webinar",
    id: "YouTubeFirstSlideWebinar",
    label: "YouTube First Slide",
    width: 1920,
    height: 1080,
    filename: "YouTube_First_Slide_Webinar",
    zoom: 2,
  },
  {
    key: "you-tube-thumbnail-webinar",
    id: "YouTubeThumbnailWebinar",
    label: "YouTube Thumbnail",
    width: 1280,
    height: 720,
    filename: "YouTube_Thumbnail_Webinar",
    zoom: 2,
  },
  {
    key: "blog-featured-webinar",
    id: "BlogFeaturedWebinar",
    label: "Blog Featured",
    width: 1280,
    height: 720,
    filename: "Blog_Featured_Webinar",
    zoom: 2,
  },
  {
    key: "blog-social-share-webinar",
    id: "BlogSocialShareWebinar",
    label: "Blog Social Share",
    width: 1280,
    height: 720,
    filename: "Blog_Social_Share_Webinar",
    zoom: 2,
  },
  {
    key: "horizontal-webinar",
    id: "HorizontalWebinar",
    label: "Facebook, LinkedIn, X",
    width: 1200,
    height: 675,
    filename: "Twitter_Facebook_LinkedIn_Webinar",
    zoom: 2,
  },
  {
    key: "stream-yard-webinar",
    id: "StreamYardWebinar",
    label: "Stream Yard Overlay",
    width: 1920,
    height: 1080,
    filename: "Stream_Yard_Overlay_Webinar",
    zoom: 2,
  },
  {
    key: "stream-yard-background-webinar",
    id: "StreamYardBackgroundWebinar",
    label: "Stream Yard Background",
    width: 1920,
    height: 1080,
    filename: "Stream_Yard_Background_Webinar",
    zoom: 2,
  },
  {
    key: "email-photo-webinar",
    id: "EmailPhotoWebinar",
    label: "Email",
    width: 1300,
    height: 288,
    nominalDimensions: "650x144",
    filename: "Email_Photo_Webinar",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "speaker-photo-webinar",
    id: "SpeakerPhotoWebinar",
    label: "Speaker Photo",
    width: 1200,
    height: 1200,
    nominalDimensions: "600x600",
    filename: "Speaker_Photo_Webinar",
    zoom: 2,
    suffix: "@2x",
  },
  {
    key: "qrcode-ipad",
    id: "QRcodeIPad",
    label: "QR Code Ipad",
    width: 2160,
    height: 1620,
    zoom: 3,
  },
  {
    key: "qrcode-full-hd",
    id: "QRcodeFullHD",
    label: "QR Code FullHD",
    width: 1920,
    height: 1080,
    zoom: 3,
  },
  {
    key: "qrcode-a-4",
    id: "QRcodeA4",
    label: "QR Code A4",
    width: 2480,
    height: 3508,
    zoom: 4,
  },
  {
    key: "qrcode-print",
    id: "QRcodePrint",
    label: "QR code 10x10cm (1181x1181)",
    width: 1181,
    height: 1181,
    zoom: 2,
  },
  {
    key: "so-me-horizontal-ads",
    id: "SoMeHorizontalAds",
    label: "SoMe Horizontal",
    width: 1200,
    height: 675,
    tooltip: "Quora, Facebook, X, LinkedIn",
    zoom: 2.2,
  },
  {
    key: "instagram-post-ads",
    id: "InstagramPostAds",
    label: "Instagram Post",
    width: 1080,
    height: 1440,
    tooltip: "Instagram Feed Post",
    zoom: 3,
  },
  {
    key: "instagram-stories-ads",
    id: "InstagramStoriesAds",
    label: "Instagram Stories",
    width: 1536,
    height: 2445,
    tooltip: "Instagram Stories",
    zoom: 4,
  },
  {
    key: "reddit-ads",
    id: "RedditAds",
    label: "Reddit",
    width: 1440,
    height: 1080,
    tooltip: "Reddit",
    zoom: 2.5,
  },
  {
    key: "daily-dev-ads",
    id: "DailyDevAds",
    label: "Daily.dev",
    width: 1024,
    height: 512,
    tooltip: "Daily.dev",
    zoom: 2,
  },
  {
    key: "reddit-thumb-1-ads",
    id: "RedditThumb1Ads",
    label: "Reddit Thumbnail 1",
    width: 400,
    height: 300,
    tooltip: "Reddit Thumbnail (with text)",
    zoom: 1,
  },
  {
    key: "reddit-thumb-2-ads",
    id: "RedditThumb2Ads",
    label: "Reddit Thumbnail 2",
    width: 400,
    height: 300,
    tooltip: "Reddit Thumbnail (image only)",
    zoom: 1,
  },
  {
    key: "blog-post-ads",
    id: "BlogPostAds",
    label: "Blog Post",
    width: 1280,
    height: 720,
    tooltip: "JB blog post banner",
    zoom: 2.2,
  },
  {
    key: "email-large-ads",
    id: "EmailLargeAds",
    label: "Email Large",
    width: 1300,
    height: 650,
    tooltip: "Email banner 650×325@2x",
    zoom: 3,
    displayDimensions: "650×325@2x",
  },
  {
    key: "email-small-ads",
    id: "EmailSmallAds",
    label: "Email Small",
    width: 1300,
    height: 400,
    tooltip: "Email banner 650×200@2x",
    zoom: 3,
    displayDimensions: "650×200@2x",
  },
  {
    key: "square-deveco-ads",
    id: "SquareDevecoAds",
    label: "Square",
    width: 1080,
    height: 1080,
    tooltip: "YouTube Community / square 1080×1080",
    zoom: 3,
  },
  {
    key: "instagram-stories-deveco-ads",
    id: "InstagramStoriesDevecoAds",
    label: "Instagram Stories",
    width: 1440,
    height: 2560,
    tooltip: "Instagram Stories (bg-only)",
    zoom: 4,
  },
  {
    key: "inline-rectangle-ads",
    id: "InlineRectangleAds",
    label: "Inline Rectangle",
    width: 600,
    height: 500,
    displayDimensions: "300×250@2x",
    tooltip: "Google Display Network — 300×250 medium rectangle",
    zoom: 1,
  },
  {
    key: "large-rectangle-ads",
    id: "LargeRectangleAds",
    label: "Large Rectangle",
    width: 672,
    height: 560,
    displayDimensions: "336×280@2x",
    tooltip: "Google Display Network — 336×280 large rectangle",
    zoom: 1.1,
  },
  {
    key: "billboard-ads",
    id: "BillboardAds",
    label: "Billboard",
    width: 1940,
    height: 500,
    displayDimensions: "970×250@2x",
    tooltip: "Google Display Network — 970×250 billboard",
    zoom: 3.6,
  },
  {
    key: "half-page-ads",
    id: "HalfPageAds",
    label: "Half-page",
    width: 600,
    height: 1200,
    displayDimensions: "300×600@2x",
    tooltip: "Google Display Network — 300×600 half-page",
    zoom: 2,
  },
  {
    key: "wide-skyscraper-ads",
    id: "WideSkyscraperAds",
    label: "Wide Skyscraper",
    width: 320,
    height: 1200,
    displayDimensions: "160×600@2x",
    tooltip: "Google Display Network — 160×600 wide skyscraper",
    zoom: 2,
  },
  {
    key: "leaderboard-970-ads",
    id: "Leaderboard970Ads",
    label: "Leaderboard 970×90",
    width: 1940,
    height: 180,
    displayDimensions: "970×90@2x",
    tooltip: "Google Display Network — 970×90 leaderboard",
    zoom: 3.6,
  },
  {
    key: "leaderboard-728-ads",
    id: "Leaderboard728Ads",
    label: "Leaderboard 728×90",
    width: 1456,
    height: 180,
    displayDimensions: "728×90@2x",
    tooltip: "Google Display Network — 728×90 leaderboard",
    zoom: 2.7,
  },
  {
    key: "leaderboard-468-ads",
    id: "Leaderboard468Ads",
    label: "Leaderboard 468×60",
    width: 936,
    height: 120,
    displayDimensions: "468×60@2x",
    tooltip: "Google Display Network — 468×60 leaderboard",
    zoom: 1.7,
  },
  {
    key: "gdn-japan-ads",
    id: "GdnJapanAds",
    label: "GDN Japan",
    width: 600,
    height: 200,
    displayDimensions: "300×100@2x",
    tooltip: "Google Display Network — 300×100 (Japan)",
    zoom: 1.1,
  },
  {
    key: "gdn-responsive-1200-x-628-ads",
    id: "GdnResponsive1200x628Ads",
    label: "Responsive Landscape",
    width: 1200,
    height: 628,
    tooltip: "Google Display Network — responsive landscape 1200×628",
    zoom: 2.2,
  },
  {
    key: "gdn-responsive-1200-x-1200-ads",
    id: "GdnResponsive1200x1200Ads",
    label: "Responsive Square",
    width: 1200,
    height: 1200,
    tooltip: "Google Display Network — responsive square 1200×1200",
    zoom: 2.2,
  },
] as const satisfies readonly TemplateDefinition[];

/**
 * The email banners and the layouts that show each one. The Email channel and
 * the Emails set of the Releases channel show the same banners on the same
 * layouts, so both read this one list.
 */
const EMAIL_MEMBERSHIPS = [
  {key: "email-01", layouts: [1, 2]},
  {key: "email-02", layouts: [1, 3]},
  {key: "email-03", layouts: [1, 3, 4]},
  {key: "email-04", layouts: [1]},
] as const satisfies readonly TemplateMembership[];

/** The products that keep their own email banners in the Releases channel. */
const AIR_PRODUCTS = ["AIR"] as const;

/** The layouts of the email banners, in ascending order. */
export const EMAIL_LAYOUTS: readonly number[] = [
  ...new Set<number>(EMAIL_MEMBERSHIPS.flatMap((membership) => membership.layouts)),
].sort((a, b) => a - b);
// The Email channel counts its layouts (`layoutsNumber`) and the Releases set
// lists them, so the two agree only when the layouts are 1 to N.
if (EMAIL_LAYOUTS.some((layout, index) => layout !== index + 1)) {
  throw new Error(`Email layouts must be 1 to N without gaps: ${EMAIL_LAYOUTS.join(", ")}`);
}

export const TEMPLATE_MEMBERSHIPS = {
  social: [
    {key: "twitter"},
    {key: "you-tube-community"},
    {key: "so-me-vertical"},
    {key: "instagram-stories"},
    {key: "instagram-feed"},
    {key: "blog-featured"},
    {key: "blog-social-share"},
  ],
  releases: [
    {key: "twitter", set: "social-media", uploadLayouts: true},
    {key: "you-tube-community", set: "social-media", uploadLayouts: true},
    {key: "so-me-vertical", set: "social-media", uploadLayouts: true},
    {key: "instagram-stories", set: "social-media", uploadLayouts: true},
    {key: "instagram-feed", set: "social-media", uploadLayouts: true},
    {key: "blog-featured", set: "social-media", uploadLayouts: true},
    {key: "blog-social-share", set: "social-media", uploadLayouts: true},
    {key: "webpage-preview", set: "webpreview", uploadLayouts: true},
    // A .NET product draws the .NET splashes below instead of these two. Each
    // place has the 2025 design (on air in code) and the 2026 design.
    {
      key: "splash",
      members: ["splash", "splash-2026"],
      set: "splash",
      uploadLayouts: true,
      notDrawnFor: DOT_NET_RELEASE_PRODUCTS,
    },
    {
      key: "splash-2-x",
      members: ["splash-2-x", "splash-2-x-2026"],
      set: "splash",
      uploadLayouts: true,
      notDrawnFor: DOT_NET_RELEASE_PRODUCTS,
    },
    // JetBrains Air keeps its own email banners (the Air skin, see
    // pages/Releases/AirBanner). Every other product shows the Email
    // channel's. The two groups share the ids Email01 to Email04.
    ...EMAIL_MEMBERSHIPS.map((membership) => ({
      ...membership,
      set: "emails",
      exceptProducts: AIR_PRODUCTS,
    })),
    {key: "releases-email-01", set: "emails", products: AIR_PRODUCTS},
    {key: "releases-email-02", set: "emails", products: AIR_PRODUCTS},
    {key: "releases-email-03", set: "emails", products: AIR_PRODUCTS},
    {key: "releases-email-04", set: "emails", products: AIR_PRODUCTS},
    {key: "horizontal-features", set: "social-media"},
    {key: "whats-new-preview", set: "whats-new"},
    {key: "wallpaper-1", set: "wallpapers"},
    {key: "wallpaper-2", set: "wallpapers"},
    {key: "wallpaper-3", set: "wallpapers"},
    {key: "wallpaper-preview", set: "wallpapers"},
    {
      // The 2026 design already uses this identity in deployed scenes.
      // Restore the previous design under a separate Archive identity.
      key: "dot-net-splash",
      members: ["dot-net-splash-legacy", "dot-net-splash"],
      sceneFallbackKey: "dot-net-splash",
      set: "splash",
      uploadLayouts: true,
      drawnFor: DOT_NET_RELEASE_PRODUCTS,
    },
    {
      key: "dot-net-splash-2-x",
      members: ["dot-net-splash-2-x-legacy", "dot-net-splash-2-x"],
      sceneFallbackKey: "dot-net-splash-2-x",
      set: "splash",
      uploadLayouts: true,
      drawnFor: DOT_NET_RELEASE_PRODUCTS,
    },
    {key: "screenshot", set: "screenshots", uploadLayouts: true},
    {key: "demo-video-intro", set: "demo-video"},
    {key: "demo-video-intro-demo", set: "demo-video"},
    {key: "demo-video-demo", set: "demo-video"},
    {key: "demo-video-code", set: "demo-video"},
  ],
  livestream: [
    {key: "livestream-horizontal"},
    {key: "livestream-square"},
    {key: "livestream-blog"},
    {key: "livestream-wide"},
    {key: "livestream-stories-intro", layouts: [6]},
    {key: "livestream-stories"},
    {key: "livestream-email"},
  ],
  internal: [{key: "blog-social-share-internal"}],
  email: EMAIL_MEMBERSHIPS,
  print: [{key: "voucher"}],
  landingpage: [{key: "landing-page"}],
  partners: [
    {key: "twitter-partners"},
    {key: "instagram-stories-partners"},
    {key: "instagram-post-partners"},
    {key: "instagram-feed-partners"},
    {key: "so-me-vertical-partners"},
  ],
  video: [{key: "you-tube-thumbnail"}],
  webinar: [
    {key: "you-tube-first-slide-webinar"},
    {key: "you-tube-thumbnail-webinar"},
    {key: "blog-featured-webinar"},
    {key: "blog-social-share-webinar"},
    {key: "horizontal-webinar"},
    {key: "stream-yard-webinar"},
    {key: "stream-yard-background-webinar"},
    {key: "email-photo-webinar"},
    {key: "speaker-photo-webinar"},
  ],
  events: [
    {key: "qrcode-ipad"},
    {key: "qrcode-full-hd"},
    {key: "qrcode-a-4"},
    {key: "qrcode-print"},
  ],
  ads: [
    {key: "inline-rectangle-ads", contentTypes: ["google-display-network"]},
    {key: "large-rectangle-ads", contentTypes: ["google-display-network"]},
    {key: "billboard-ads", contentTypes: ["google-display-network"]},
    {key: "half-page-ads", contentTypes: ["google-display-network"]},
    {key: "wide-skyscraper-ads", contentTypes: ["google-display-network"]},
    {key: "leaderboard-970-ads", contentTypes: ["google-display-network"]},
    {key: "leaderboard-728-ads", contentTypes: ["google-display-network"]},
    {key: "leaderboard-468-ads", contentTypes: ["google-display-network"]},
    {key: "gdn-japan-ads", contentTypes: ["google-display-network"]},
    {
      key: "gdn-responsive-1200-x-628-ads",
      contentTypes: ["google-display-network"],
      layouts: [1, 2, 3],
    },
    {
      key: "gdn-responsive-1200-x-1200-ads",
      contentTypes: ["google-display-network"],
      layouts: [1, 2, 3],
    },
    {key: "so-me-horizontal-ads", contentTypes: ["ai", "deveco"]},
    {key: "instagram-post-ads", contentTypes: ["ai", "deveco"]},
    {key: "instagram-stories-ads", contentTypes: ["ai"]},
    {key: "reddit-ads", contentTypes: ["ai"]},
    {key: "daily-dev-ads", contentTypes: ["ai"]},
    {key: "reddit-thumb-1-ads", contentTypes: ["ai"]},
    {key: "reddit-thumb-2-ads", contentTypes: ["ai"]},
    {key: "blog-post-ads", contentTypes: ["deveco"]},
    {key: "email-large-ads", contentTypes: ["deveco"]},
    {key: "email-small-ads", contentTypes: ["deveco"]},
    {key: "square-deveco-ads", contentTypes: ["deveco"]},
    {key: "instagram-stories-deveco-ads", contentTypes: ["deveco"]},
  ],
} as const satisfies Record<string, readonly TemplateMembership[]>;

/** A template id names a design in CSS (`#Id`, `[id^="Id-"]`) and in instance ids (`Id-CODE`). */
const TEMPLATE_ID_PATTERN = /^[A-Za-z0-9]+$/;

const DEFINITIONS_BY_KEY = new Map<string, TemplateDefinition>();
for (const definition of TEMPLATE_DEFINITIONS) {
  if (DEFINITIONS_BY_KEY.has(definition.key)) {
    throw new Error(`Duplicate template key "${definition.key}"`);
  }
  if (!TEMPLATE_ID_PATTERN.test(definition.id)) {
    throw new Error(`Template "${definition.key}" has id "${definition.id}": use letters and digits only`);
  }
  if (
    !Number.isInteger(definition.width) ||
    definition.width <= 0 ||
    !Number.isInteger(definition.height) ||
    definition.height <= 0
  ) {
    throw new Error(`Template "${definition.key}" has invalid dimensions`);
  }
  if (typeof definition.zoom !== "number" || !Number.isFinite(definition.zoom)) {
    throw new Error(`Template "${definition.key}" has invalid zoom`);
  }
  DEFINITIONS_BY_KEY.set(definition.key, definition);
}

/** The definition of a template key. */
export function getTemplateDefinitionByKey(key: string): TemplateDefinition {
  const definition = DEFINITIONS_BY_KEY.get(key);
  if (!definition) throw new Error(`Unknown template key "${key}"`);
  return definition;
}

/** Does the product see the template? Without a product scope, every product does. */
export function templateOffersProduct(template: TemplateMembership, product: string): boolean {
  if (template.products) return template.products.includes(product);
  if (template.exceptProducts) return !template.exceptProducts.includes(product);
  return true;
}

/** Does a product's page draw the template (`drawnFor` / `notDrawnFor`)? */
export function isDrawnForProduct(
  template: Pick<TemplateMembership, "drawnFor" | "notDrawnFor">,
  product: string
): boolean {
  if (template.drawnFor) return template.drawnFor.includes(product);
  if (template.notDrawnFor) return !template.notDrawnFor.includes(product);
  return true;
}

/** Is there a product that sees both templates? */
function productScopesOverlap(a: TemplateMembership, b: TemplateMembership): boolean {
  if (a.products) return a.products.some((product) => templateOffersProduct(b, product));
  if (b.products) return b.products.some((product) => templateOffersProduct(a, product));
  return true;
}

function buildMetadata(
  channel: string,
  membership: TemplateMembership,
  key: string,
  members: readonly string[]
): TemplateMetadata {
  const definition = DEFINITIONS_BY_KEY.get(key);
  if (!definition) {
    throw new Error(`Template membership "${channel}" references unknown key "${key}"`);
  }
  if (membership.products && membership.exceptProducts) {
    throw new Error(`Template "${channel}/${key}" has both products and exceptProducts`);
  }
  if (membership.drawnFor && membership.notDrawnFor) {
    throw new Error(`Template "${channel}/${key}" has both drawnFor and notDrawnFor`);
  }
  return {
    ...definition,
    ...membership,
    key,
    members,
    memberIds: members.map((member) => {
      const memberDefinition = DEFINITIONS_BY_KEY.get(member);
      if (!memberDefinition) {
        throw new Error(`Template membership "${channel}" references unknown key "${member}"`);
      }
      return memberDefinition.id;
    }),
    dimensions: definition.nominalDimensions ?? formatTemplateDimensions(definition),
  };
}

/**
 * The slots of a channel, in channel order. Exported for tests, which build
 * slots from their own memberships.
 */
export function buildTemplateSlots(
  channel: string,
  memberships: readonly TemplateMembership[]
): TemplateSlot[] {
  const placed = new Set<string>();
  return memberships.map((membership, index) => {
    const keys = membership.members ?? [membership.key];
    if (!keys.includes(membership.key)) {
      throw new Error(`Template slot "${channel}/${membership.key}" does not list its key in members`);
    }
    const sceneFallbackKey = membership.sceneFallbackKey ?? keys[0];
    if (!keys.includes(sceneFallbackKey)) {
      throw new Error(`Template slot "${channel}/${membership.key}" does not list its scene fallback in members`);
    }
    for (const key of keys) {
      // One place per design in a channel: a slot choice then names one place.
      if (placed.has(key)) throw new Error(`Template "${key}" has two places in channel "${channel}"`);
      placed.add(key);
    }
    const members = keys.map((key) => buildMetadata(channel, membership, key, keys));
    return {
      channel, index, members,
      defaultMember: members[keys.indexOf(membership.key)],
      sceneFallbackMember: members[keys.indexOf(sceneFallbackKey)],
    };
  });
}

/** Every slot of every channel. */
export const TEMPLATE_SLOTS_BY_CHANNEL: Record<string, TemplateSlot[]> = Object.fromEntries(
  Object.entries(TEMPLATE_MEMBERSHIPS).map(([channel, memberships]) => [
    channel,
    buildTemplateSlots(channel, memberships),
  ])
);

/**
 * Every design of every slot: the identity of the templates. Saved scenes,
 * previews, crops and file names read this, so a design that is not on air
 * keeps working.
 */
export const KNOWN_TEMPLATES_BY_CHANNEL: Record<string, TemplateMetadata[]> = Object.fromEntries(
  Object.entries(TEMPLATE_SLOTS_BY_CHANNEL).map(([channel, slots]) => [
    channel,
    slots.flatMap((slot) => slot.members),
  ])
);

/**
 * The designs that code puts on air, one per slot. A page that follows the
 * superadmin's choice reads drawnTemplates (data/templateSlots.ts) instead.
 */
export const TEMPLATES_BY_CHANNEL: Record<string, TemplateMetadata[]> = Object.fromEntries(
  Object.entries(TEMPLATE_SLOTS_BY_CHANNEL).map(([channel, slots]) => [
    channel,
    slots.map((slot) => slot.defaultMember),
  ])
);

const METADATA_BY_CHANNEL_AND_ID = new Map<string, TemplateMetadata[]>();
for (const [channel, templates] of Object.entries(KNOWN_TEMPLATES_BY_CHANNEL)) {
  for (const template of templates) {
    const identity = `${channel}/${template.id}`;
    const same = METADATA_BY_CHANNEL_AND_ID.get(identity) ?? [];
    if (same.some((other) => productScopesOverlap(other, template))) {
      throw new Error(`Duplicate template identity "${identity}"`);
    }
    METADATA_BY_CHANNEL_AND_ID.set(identity, [...same, template]);
  }
}

/**
 * The template a channel shows under an id, on air or not. An id that two
 * templates share (each for its own products) needs the product.
 */
export function getTemplateMetadata(
  channel: string,
  id: string,
  product?: string | null
): TemplateMetadata {
  const candidates = METADATA_BY_CHANNEL_AND_ID.get(`${channel}/${id}`);
  if (!candidates) throw new Error(`Unknown template "${id}" in channel "${channel}"`);
  if (candidates.length === 1) return candidates[0];
  if (!product) {
    throw new Error(`Template "${id}" in channel "${channel}" differs by product; give the product`);
  }
  const match = candidates.find((template) => templateOffersProduct(template, product));
  if (!match) throw new Error(`No template "${id}" in channel "${channel}" for product "${product}"`);
  return match;
}

export function getTemplateSize(channel: string, id: string, product?: string | null): TemplateSize {
  const {width, height} = getTemplateMetadata(channel, id, product);
  return {width, height};
}

/**
 * Resolve a runtime instance ID such as `WhatsNewPreview-PC` to its template.
 * Exact public IDs win. A suffixed ID must match exactly one channel template.
 */
export function getTemplateInstanceMetadata(
  channel: string,
  instanceId: string,
  product?: string | null
): TemplateMetadata {
  if (METADATA_BY_CHANNEL_AND_ID.has(`${channel}/${instanceId}`)) {
    return getTemplateMetadata(channel, instanceId, product);
  }

  const ids = KNOWN_TEMPLATE_IDS_BY_CHANNEL[channel]?.filter((id) => instanceId.startsWith(`${id}-`));
  if (ids?.length === 1) return getTemplateMetadata(channel, ids[0], product);
  if (ids && ids.length > 1) {
    throw new Error(`Ambiguous template instance "${instanceId}" in channel "${channel}"`);
  }
  throw new Error(`Unknown template instance "${instanceId}" in channel "${channel}"`);
}

export function getTemplateInstanceSize(
  channel: string,
  instanceId: string,
  product?: string | null
): TemplateSize {
  const {width, height} = getTemplateInstanceMetadata(channel, instanceId, product);
  return {width, height};
}

export function formatTemplateDimensions({width, height}: TemplateSize): string {
  return `${width}x${height}`;
}

/** The templates of a list that a product sees: one per id, in list order. */
export function templatesOfProduct(
  templates: readonly TemplateMetadata[],
  product: string
): TemplateMetadata[] {
  return templates.filter((template) => templateOffersProduct(template, product));
}

/** The templates that code puts on air in a channel for a product: one per id, in channel order. */
export function templatesForProduct(channel: string, product: string): TemplateMetadata[] {
  const templates = TEMPLATES_BY_CHANNEL[channel];
  if (!templates) throw new Error(`Unknown channel "${channel}"`);
  return templatesOfProduct(templates, product);
}

/** Every design of a channel that a product sees, on air or not, in channel order. */
export function knownTemplatesForProduct(channel: string, product: string): TemplateMetadata[] {
  const templates = KNOWN_TEMPLATES_BY_CHANNEL[channel];
  if (!templates) throw new Error(`Unknown channel "${channel}"`);
  return templatesOfProduct(templates, product);
}

const uniqueIds = (templates: readonly {id: string}[]): string[] => [
  ...new Set(templates.map(({id}) => id)),
];

/** Every id of a channel that code puts on air, once, whichever products see it. */
export const TEMPLATE_IDS_BY_CHANNEL: Record<string, string[]> = Object.fromEntries(
  Object.entries(TEMPLATES_BY_CHANNEL).map(([channel, entries]) => [channel, uniqueIds(entries)])
);

/** Every id of a channel, on air or not, once. */
export const KNOWN_TEMPLATE_IDS_BY_CHANNEL: Record<string, string[]> = Object.fromEntries(
  Object.entries(KNOWN_TEMPLATES_BY_CHANNEL).map(([channel, entries]) => [
    channel,
    uniqueIds(entries),
  ])
);

/**
 * The banners each releases set draws, by set: every id once, whichever
 * products see it. An opt-in banner is in no set. A releases banner must have
 * a set or be opt-in, and not both: the manifest must say who draws it.
 * `templates` is a drawn list of the releases channel.
 */
export function releasesSetTemplateIds(
  templates: readonly TemplateMetadata[]
): Record<string, string[]> {
  const bySet = templates.reduce<Record<string, TemplateMetadata[]>>((sets, template) => {
    if (template.optIn) {
      if (template.set) {
        throw new Error(`Opt-in releases template "${template.id}" has a set`);
      }
      return sets;
    }
    if (!template.set) throw new Error(`Releases template "${template.id}" has no set`);
    (sets[template.set] ||= []).push(template);
    return sets;
  }, {});
  return Object.fromEntries(Object.entries(bySet).map(([set, list]) => [set, uniqueIds(list)]));
}

/** The releases banners of no set, in the channel's order: every id once. */
export function releasesOptInTemplateIds(templates: readonly TemplateMetadata[]): string[] {
  return uniqueIds(templates.filter((template) => template.optIn));
}

/** The ids of each releases set that code puts on air. */
export const RELEASES_SET_TEMPLATE_IDS: Record<string, string[]> = releasesSetTemplateIds(
  TEMPLATES_BY_CHANNEL.releases
);

export const RELEASES_OPT_IN_TEMPLATE_IDS: string[] = releasesOptInTemplateIds(
  TEMPLATES_BY_CHANNEL.releases
);
